import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../../../axios";
import { errorToast, successToast } from "../../../utils/toast";
import {
  AiOutlineArrowRight,
  AiOutlineArrowLeft,
  AiOutlineCheckCircle,
  AiOutlineUndo,
  AiOutlineDownload,
  AiOutlineCopy,
} from "react-icons/ai";
import moment from "moment";

const getRequestUserId = (request) =>
  request?.id ||
  request?.user?.id ||
  request?.user?._id ||
  request?.user ||
  request?.userId?.id ||
  request?.userId?._id ||
  request?.userId ||
  request?.createdBy?._id ||
  request?.createdBy ||
  request?.userDetails?._id;

const UnifiedWithdrawals = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentType, setPaymentType] = useState("all"); // all | bank | upi

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const endpoint =
        activeTab === "pending"
          ? "getUnifiedWithdrawalRequests"
          : "getUnifiedSuccessPayments";

      const { data } = await axios.get(
        `/${endpoint}?page=${page}&limit=10&query=${searchQuery}`,
      );

      if (data?.result) {
        setRequests(data.result.allRequests || []);
        setTotalPages(data.result.pagination?.totalPagesCount || 1);
        setTotalAmount(data.result.wholeAmount || 0);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      errorToast(error.response?.data?.message || "Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
  switch (paymentType) {
    case "upi":
      return requests.filter(
        (item) => item.upiId && item.upiId.trim() !== ""
      );

    case "bank":
      return requests.filter(
        (item) => item.accountNo && item.accountNo.trim() !== ""
      );

    default:
      return requests;
  }
}, [requests, paymentType]);

  useEffect(() => {
    fetchRequests();
    setSelectedIds([]); // Clear selection when tab/page changes
  }, [activeTab, page, searchQuery]);

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === requests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(requests.map((req) => req._id));
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) {
      return errorToast("UPI ID not available");
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const input = document.createElement("textarea");
        input.value = text;
        input.setAttribute("readonly", "");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }

      successToast("UPI ID copied");
    } catch (error) {
      console.error("Failed to copy UPI ID:", error);
      errorToast("Failed to copy UPI ID");
    }
  };

  // Confirmation Modal State
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const confirmAction = (title, message, action) => {
    setModal({
      isOpen: true,
      title,
      message,
      onConfirm: async () => {
        await action();
        closeModal();
      },
    });
  };

  const markAsPaid = (id = null) => {
    const action = async () => {
      try {
        if (id) {
          await axios.post("/markUnifiedPaymentDone", { paymentId: id });
        } else {
          if (selectedIds.length === 0)
            return errorToast("Select at least one request");
          await axios.post("/markUnifiedPaymentDoneAll", {
            selected: selectedIds,
          });
        }
        successToast("Marked as paid successfully");
        fetchRequests();
        setSelectedIds([]);
      } catch (error) {
        errorToast(error.response?.data?.message || "Failed to mark as paid");
      }
    };

    confirmAction(
      "Mark as Paid?",
      `Are you sure you want to mark ${id ? "this request" : `${selectedIds.length} requests`
      } as paid? This action cannot be undone easily.`,
      action,
    );
  };

  const revertPayment = (id, type) => {
    const action = async () => {
      try {
        const endpoint =
          type === "pending"
            ? "revertUnifiedWithdrawal"
            : "revertUnifiedSuccessPayment";

        await axios.post(`/${endpoint}`, { paymentId: id });
        successToast(
          type === "pending"
            ? "Request reverted and coins restored"
            : "Payment moved back to pending"
        );
        fetchRequests();
      } catch (error) {
        errorToast(error.response?.data?.message || "Failed to revert");
      }
    };

    confirmAction(
      type === "pending" ? "Revert & Restore Coins?" : "Move to Pending?",
      type === "pending"
        ? "Are you sure you want to revert this request? The coins will be refunded to the user's wallet."
        : "Are you sure you want to move this payment back to pending status?",
      action
    );
  };

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.append("query", searchQuery);

      // If users are selected, pass selected IDs to backend
//       if (selectedIds.length > 0) {
// const ids = filteredRequests.map((x) => x._id);

// params.append("selectedIds", JSON.stringify(ids));
//       }
// Agar checkbox select kiye hain to unhi ko export karo
    // warna current filter ke sab users export karo
    const exportIds =
      selectedIds.length > 0
        ? selectedIds
        : filteredRequests.map((item) => item._id);
        params.append("selectedIds", JSON.stringify(exportIds));

      const response = await axios.get(
        `/exportPendingWithdrawalsExcel?${params.toString()}`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AW${moment().format("DDMMYYYY")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      successToast("Excel file downloaded successfully");
    } catch (error) {
      errorToast("Failed to download Excel");
    }
  };



  return (
    <div className="p-6 pt-10 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Unified Withdrawals
        </h1>
        <div className="text-gray-600 font-medium">
          Total Amount:{" "}
          <span className="text-green-600 font-bold">₹{totalAmount}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex mb-6 bg-white rounded-lg p-1 shadow-sm w-fit">
        <button
          className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === "pending"
            ? "bg-purple text-white"
            : "text-gray-600 hover:bg-gray-100"
            }`}
          onClick={() => {
            setActiveTab("pending");
            setPage(1);
          }}
        >
          Pending Requests
        </button>
        <button
          className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === "success"
            ? "bg-green-600 text-white"
            : "text-gray-600 hover:bg-gray-100"
            }`}
          onClick={() => {
            setActiveTab("success");
            setPage(1);
          }}
        >
          Success History
        </button>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
        <input
          type="text"
          placeholder="Search by Name or Email..."
          className="px-4 py-2 border rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-purple-500"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

<div className="flex mb-6 bg-white rounded-lg p-1 shadow-sm w-fit">
  <button
    className={`px-6 py-2 rounded-md font-medium transition-colors ${
      paymentType === "all"
        ? "bg-purple text-white"
        : "text-gray-600 hover:bg-gray-100"
    }`}
    onClick={() => {
      setPaymentType("all");
      setPage(1);
    }}
  >
    All
  </button>

  <button
    className={`px-6 py-2 rounded-md font-medium transition-colors ${
      paymentType === "bank"
        ? "bg-blue-600 text-white"
        : "text-gray-600 hover:bg-gray-100"
    }`}
    onClick={() => {
      setPaymentType("bank");
      setPage(1);
    }}
  >
    Account Number
  </button>

  <button
    className={`px-6 py-2 rounded-md font-medium transition-colors ${
      paymentType === "upi"
        ? "bg-green-600 text-white"
        : "text-gray-600 hover:bg-gray-100"
    }`}
    onClick={() => {
      setPaymentType("upi");
      setPage(1);
    }}
  >
    UPI ID
  </button>
</div>
        <div className="flex gap-2">
          {activeTab === "pending" && (
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
            >
              <AiOutlineDownload /> Export to Excel
            </button>
          )}

          {activeTab === "pending" && selectedIds.length > 0 && (
            <button
              onClick={() => markAsPaid()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
            >
              <AiOutlineCheckCircle /> Mark {selectedIds.length} as Paid
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-gray-700 font-semibold uppercase text-xs border-b">
              <tr>
                {activeTab === "pending" && (
                  <th className="p-4 w-10">
                    <input
                      type="checkbox"
                      checked={
                       filteredRequests.length > 0 &&
                        selectedIds.length === filteredRequests.length
                      }
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-purple focus:ring-purple"
                    />
                  </th>
                )}
                <th className="p-4">Date</th>
                <th className="p-4">User</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Deduction Details</th>
                <th className="p-4">Bank Details</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-500">
                    No requests found
                  </td>
                </tr>
              ) : (
                filteredRequests.map((req) => {
                  const userId = getRequestUserId(req);
                  console.log(userId,"userIdd")

                  return (
                  <tr
                    key={req._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {activeTab === "pending" && (
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(req._id)}
                          onChange={() => handleSelect(req._id)}
                          className="rounded border-gray-300 text-purple focus:ring-purple"
                        />
                      </td>
                    )}
                    <td className="p-4 whitespace-nowrap">
                      <div>{moment(req.createdOn).format("DD MMM YYYY")}</div>
                      <div className="text-xs text-gray-400">
                        {moment(req.createdOn).format("hh:mm A")}
                      </div>
                    </td>
                    <td className="p-4">
                      {userId ? (
                        <Link
                          to={`/dash/view-user/${userId}`}
                          className="font-medium text-gray-900 hover:underline"
                        >
                          {req.name}
                        </Link>
                      ) : (
                        <div className="font-medium text-gray-900">
                          {req.name}
                        </div>
                      )}
                      <div className="text-xs">{req.email}</div>
                      <div className="text-xs">{req.mobile}</div>
                    </td>
                    <td className="p-4 font-bold text-gray-800">
                      ₹{req.amount}
                    </td>
                    <td className="p-4 text-xs">
                      {req.deductedFrom?.contestCoins > 0 && (
                        <div className="text-orange-600">
                          Contest: {req.deductedFrom.contestCoins}
                        </div>
                      )}
                      {req.deductedFrom?.referralCoins > 0 && (
                        <div className="text-blue-600">
                          Referral: {req.deductedFrom.referralCoins}
                        </div>
                      )}
                      {req.deductedFrom?.questionCoins > 0 && (
                        <div className="text-green-600">
                          Question: {req.deductedFrom.questionCoins}
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-xs max-w-xs truncate">
                      {req.upiId ? (
                        <div className="flex items-center gap-2">
                          <div className="min-w-0">
                            <span className="font-semibold">UPI:</span>{" "}
                            <span className="font-mono">{req.upiId}</span>
                          </div>
                          <button
                            type="button"
                            title="Copy UPI ID"
                            onClick={() => copyToClipboard(req.upiId)}
                            className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-purple hover:text-white transition shrink-0"
                          >
                            <AiOutlineCopy size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <span className="font-semibold">Bank:</span>{" "}
                            {req.bankName}
                          </div>
                          <div>
                            <span className="font-semibold">Acc:</span>{" "}
                            {req.accountNo}
                          </div>
                          <div>
                            <span className="font-semibold">IFSC:</span>{" "}
                            {req.ifsc}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-2">
                        {activeTab === "pending" ? (
                          <>
                            <button
                              onClick={() => markAsPaid(req._id)}
                              title="Mark as Paid"
                              className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition"
                            >
                              <AiOutlineCheckCircle size={18} />
                            </button>
                            <button
                              onClick={() => revertPayment(req._id, "pending")}
                              title="Revert & Restore Coins"
                              className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition"
                            >
                              <AiOutlineUndo size={18} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => revertPayment(req._id, "success")}
                            title="Move back to Pending"
                            className="p-2 bg-yellow-100 text-yellow-600 rounded-full hover:bg-yellow-200 transition"
                          >
                            <AiOutlineUndo size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center bg-gray-50">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-4 py-2 border rounded-lg bg-white disabled:opacity-50 hover:bg-gray-100 text-sm font-medium"
            >
              Previous
            </button>
            <span className="text-sm font-medium text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 border rounded-lg bg-white disabled:opacity-50 hover:bg-gray-100 text-sm font-medium"
            >
              Next
            </button>
          </div>
        )}
      </div>
      {/* Confirmation Modal */}
      {modal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6 transform transition-all scale-100">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {modal.title}
            </h3>
            <p className="text-gray-600 mb-6">{modal.message}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={modal.onConfirm}
                className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedWithdrawals;
